import {useState, useRef, useEffect, useContext} from "react";
import {
    Box, Stack, Typography, Card, CardContent, TextField, Button, IconButton,
    Chip, Select, MenuItem, Slider, Switch, Divider, CircularProgress,
} from "@mui/material";
import {
    IconSend, IconCopy, IconCircleCheck, IconClock, IconAdjustments,
    IconMessage, IconRotateClockwise2, IconFileText,
} from "@tabler/icons-react";
import {Context} from "../../../../context/AppContext";

const cardSx = {borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none"};

const MODE_INFO = [
    {mode: "Naive", desc: "Direct vector similarity search on chunks"},
    {mode: "Local", desc: "Entity-centric graph neighborhood search"},
    {mode: "Global", desc: "Community-level summarization search"},
    {mode: "Hybrid", desc: "Combines naive vector + graph search"},
    {mode: "Mix", desc: "Integrates local + global graph search"},
];

export const QueryInterface = ({topic}) => {
    const [query, setQuery] = useState("");
    const [mode, setMode] = useState("hybrid");
    const [topK, setTopK] = useState(topic.config.topK);
    const [onlyNeedContext, setOnlyNeedContext] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [isQuerying, setIsQuerying] = useState(false);
    const [currentResult, setCurrentResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const resultRef = useRef(null);

    const {state, executeKbQuery} = useContext(Context);
    const history = state.kbWorkspace[topic.id]?.queries ?? [];

    const handleQuery = async () => {
        if (!query.trim() || isQuerying) return;
        setIsQuerying(true);
        setCurrentResult(null);

        try {
            const result = await executeKbQuery(topic.id, {
                query: query.trim(),
                mode,
                topK,
                onlyNeedContext,
            });
            setCurrentResult(result);
        } catch (err) {
        } finally {
            setIsQuerying(false);
        }
    };

    const handleCopy = () => {
        if (currentResult) {
            navigator.clipboard.writeText(currentResult.response);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    useEffect(() => {
        if (currentResult && resultRef.current) {
            resultRef.current.scrollIntoView({behavior: "smooth"});
        }
    }, [currentResult]);

    return (
        <Stack gap={2}>

            <Card sx={cardSx}>
                <CardContent sx={{pt: 2, pb: 2}}>
                    <Stack direction={"row"} gap={1}>
                        <TextField
                            fullWidth size={"small"}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={"Ask a question about the documents in this topic..."}
                            disabled={isQuerying}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleQuery();
                                }
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "divider",
                                        borderWidth: "1px",
                                    },
                                },
                            }}
                        />
                        <IconButton
                            onClick={() => setShowSettings(!showSettings)}
                            sx={{
                                border: "1px solid", borderColor: "divider", borderRadius: 1.5,
                                bgcolor: showSettings ? "action.selected" : "transparent",
                            }}
                        >
                            <IconAdjustments size={16}/>
                        </IconButton>
                        <Button
                            startIcon={isQuerying ? <CircularProgress size={14} sx={{color: "#fff"}}/> :
                                <IconSend size={14}/>}
                            onClick={handleQuery}
                            disabled={!query.trim() || isQuerying}
                            sx={{
                                bgcolor: "#000",
                                color: "#fff",
                                fontSize: 12,
                                px: 2,
                                py: 1,
                                textTransform: "none",
                                borderRadius: 2,
                                boxShadow: "none",
                                whiteSpace: "nowrap",
                                "&:hover": {bgcolor: "#000"},
                                "&.Mui-disabled": {
                                    bgcolor: "action.disabledBackground",
                                    color: "text.disabled",
                                },
                            }}
                        >
                            Query
                        </Button>
                    </Stack>

                    {showSettings && (
                        <Box sx={{mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider"}}>
                            <Stack direction={"row"} gap={2} flexWrap={"wrap"} sx={{mb: 1.5}}>
                                <Box sx={{flex: 1, minWidth: 140}}>
                                    <Typography fontSize={12} sx={{mb: 0.75}}>Search Mode</Typography>
                                    <Select fullWidth size={"small"} value={mode}
                                            onChange={(e) => setMode(e.target.value)}>
                                        <MenuItem value={"naive"}>Naive</MenuItem>
                                        <MenuItem value={"local"}>Local</MenuItem>
                                        <MenuItem value={"global"}>Global</MenuItem>
                                        <MenuItem value={"hybrid"}>Hybrid</MenuItem>
                                        <MenuItem value={"mix"}>Mix</MenuItem>
                                    </Select>
                                </Box>
                                <Box sx={{flex: 1, minWidth: 140}}>
                                    <Typography fontSize={12} sx={{mb: 1}}>Top K: {topK}</Typography>
                                    <Slider
                                        value={topK}
                                        onChange={(e, v) => setTopK(v)}
                                        min={1} max={200} step={1} size={"small"}
                                        sx={{color: "#000"}}
                                    />
                                </Box>
                                <Box sx={{flex: 1, minWidth: 140}}>
                                    <Typography fontSize={12} sx={{mb: 0.75}}>Only Need Context</Typography>
                                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                                        <Switch checked={onlyNeedContext}
                                                onChange={(e) => setOnlyNeedContext(e.target.checked)}/>
                                        <Typography fontSize={11} color={"text.secondary"}>
                                            {onlyNeedContext ? "On" : "Off"}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </Stack>
                            <Box sx={{bgcolor: "action.hover", borderRadius: 1.5, p: 1.5}}>
                                <Stack gap={0.4}>
                                    {MODE_INFO.map(m => (
                                        <Typography key={m.mode} fontSize={11} color={"text.secondary"}>
                                            <Box component={"strong"}
                                                 sx={{color: "text.primary"}}>{m.mode}:</Box> {m.desc}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {isQuerying && (
                <Card sx={cardSx}>
                    <CardContent sx={{py: 4, textAlign: "center"}}>
                        <CircularProgress size={22} sx={{mb: 1.5}}/>
                        <Typography fontSize={13} color={"text.secondary"}>
                            Querying knowledge base using <strong>{mode}</strong> mode...
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {currentResult && !isQuerying && (
                <Card sx={cardSx} ref={resultRef}>
                    <Box sx={{px: 3, pt: 2, pb: 1}}>
                        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                            <Stack direction={"row"} alignItems={"center"} gap={1}>
                                <IconMessage size={16}/>
                                <Typography fontWeight={600} fontSize={14}>Response</Typography>
                            </Stack>
                            <Stack direction={"row"} alignItems={"center"} gap={1}>
                                <Chip label={currentResult.mode} variant={"outlined"} size={"small"}
                                      sx={{fontSize: 10}}/>
                                {!!currentResult.tokensUsed && (
                                    <Chip label={`${currentResult.tokensUsed} tokens`} variant={"outlined"}
                                          size={"small"} sx={{fontSize: 10}}/>
                                )}
                                <IconButton size={"small"} onClick={handleCopy}>
                                    {copied ? <IconCircleCheck size={16} color={"#16a34a"}/> : <IconCopy size={16}/>}
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Box>
                    <CardContent sx={{pt: 1}}>
                        <Box sx={{bgcolor: "action.hover", borderRadius: 1.5, p: 2, mb: 1.5}}>
                            <Typography fontSize={12} color={"text.secondary"} sx={{mb: 0.5}}>Query:</Typography>
                            <Typography fontSize={13}>{currentResult.query}</Typography>
                        </Box>
                        <Typography fontSize={13} sx={{whiteSpace: "pre-wrap", lineHeight: 1.6}}>
                            {currentResult.response}
                        </Typography>
                        {currentResult.sources.length > 0 && (
                            <Box sx={{mt: 2, pt: 1.5, borderTop: "1px solid", borderColor: "divider"}}>
                                <Stack direction={"row"} alignItems={"center"} gap={0.5} sx={{mb: 1}}>
                                    <IconFileText size={12}/>
                                    <Typography fontSize={11} color={"text.secondary"}>Sources</Typography>
                                </Stack>
                                <Stack direction={"row"} gap={0.75} flexWrap={"wrap"}>
                                    {currentResult.sources.map(src => (
                                        <Chip key={src} label={src} size={"small"} sx={{fontSize: 10}}/>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                <Button
                    startIcon={<IconClock size={14}/>}
                    onClick={() => setShowHistory(!showHistory)}
                    sx={{textTransform: "none", fontSize: 12, color: "text.secondary"}}
                >
                    Query History ({history.length})
                </Button>
                {history.length > 0 && (
                    <Button
                        startIcon={<IconRotateClockwise2 size={13}/>}
                        onClick={() => {
                            setCurrentResult(null);
                            setQuery("");
                        }}
                        sx={{textTransform: "none", fontSize: 12, color: "text.secondary"}}
                    >
                        Clear
                    </Button>
                )}
            </Stack>

            {showHistory && history.length > 0 && (
                <Card sx={cardSx}>
                    <Box sx={{maxHeight: 300, overflow: "auto"}}>
                        {history.slice(0, 20).map((qr, idx) => (
                            <Box key={qr.id}>
                                {idx > 0 && <Divider/>}
                                <Box
                                    sx={{p: 1.5, cursor: "pointer", "&:hover": {bgcolor: "action.hover"}}}
                                    onClick={() => {
                                        setQuery(qr.query);
                                        setCurrentResult(qr);
                                    }}
                                >
                                    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}
                                           sx={{mb: 0.5}}>
                                        <Typography fontSize={12} noWrap sx={{flex: 1}}>{qr.query}</Typography>
                                        <Stack direction={"row"} alignItems={"center"} gap={1}
                                               sx={{ml: 1, flexShrink: 0}}>
                                            <Chip label={qr.mode} variant={"outlined"} size={"small"}
                                                  sx={{fontSize: 9, height: 16}}/>
                                            <Typography fontSize={10} color={"text.secondary"}>
                                                {new Date(qr.timestamp).toLocaleTimeString()}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Typography fontSize={11} color={"text.secondary"} noWrap>
                                        {qr.response.slice(0, 120)}...
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Card>
            )}

        </Stack>
    );
};